import { PayloadAction } from "@reduxjs/toolkit";
import { eventChannel, END } from "redux-saga";
import { takeLatest, all, put, take, fork, call } from "redux-saga/effects";

import { IProgressState } from "infrastructure/helpers";
import { HttpError, HttpStatusCodes } from "infrastructure/http";
import { notification } from "infrastructure/notification";
import { createNotificationResult } from "infrastructure/store";
import { glossaryMessages } from "ui/screens/plex-dictionary/plex-dictionary.localization";

import { termTranslationsUploadActions } from "./TermTranslationsUpload.actions";
import { termTranslationsUploadApi as api } from "./TermTranslationsUpload.api";
import { getUploadValidationError } from "./TermTranslationsUpload.helpers";

const createUploadChannel = (file: File) => {
  let emit;

  const channel = eventChannel(emitter => {
    emit = emitter;

    return () => {};
  });

  const promise = call(api.uploadTranslations, file, (progress: IProgressState) => {
    emit(progress.value);

    if (progress.completed) {
      emit(END);
    }
  });

  return [promise, channel];
};

function* progressListener(channel) {
  while (true) {
    const data = yield take(channel);

    yield put(termTranslationsUploadActions.uploadTranslationsProcessing(data));
  }
}

function* uploadTranslationsExecute(action: PayloadAction<File[]>) {
  try {
    const file = action.payload[0];

    if (file != null) {
      const [promise, channel] = createUploadChannel(file);
      yield fork(progressListener, channel);

      yield promise;

      yield put(termTranslationsUploadActions.uploadTranslationsCompleted());
    } else {
      yield put(termTranslationsUploadActions.uploadTranslationsError(notification.error().useMessageId(glossaryMessages.error("termTranslationsUploadFileRequired"))));
    }
  } catch (error) {
    if (error instanceof HttpError) {
      if (error.statusCode === HttpStatusCodes.BadRequest) {
        yield put(
          termTranslationsUploadActions.uploadTranslationsFailed(
            createNotificationResult(getUploadValidationError(error.data)).withNotification(notification.error().useMessageId(glossaryMessages.error("termTranslationsUpload")))
          )
        );
      }
    } else {
      yield put(termTranslationsUploadActions.uploadTranslationsError(notification.error().useMessageId(glossaryMessages.error("termTranslationsUploadUnexpected"))));
    }
  }
}

function* actionWatcher() {
  yield takeLatest(termTranslationsUploadActions.uploadTranslations.toString(), uploadTranslationsExecute);
}

export function* termTranslationsUploadSaga() {
  yield all([actionWatcher()]);
}
